import type { 
  OrchestratorConfig, 
  OrchestratorInstance, 
  OrchestratorState,
  DataMessage
} from '../types';

import { MessageTypes, MessageSources } from '../types';

import { MessageBridge } from './MessageBridge';
import { IframeManager, setIframeStateClass } from './IframeManager';
import { ConfigManager } from './ConfigManager';
import { waitForDOMReady, addEventListener, createLogger, ErrorHandler, debounce } from '../utils';
import { REQUEST_TIMEOUT, VERSION } from '../constants';

/**
 * Discriminator for the 'iframe-ready' event payload, identifying which
 * internal code path triggered the emission.
 */
export type IframeReadySource = 'load' | 'ready' | 'widget-state-fallback';

/**
 * Main orchestrator class that manages the entire chat integration
 * 
 * This class coordinates between the configuration manager, message bridge, and iframe manager
 * to provide a complete chat interface integration. It handles the lifecycle of the chat widget
 * including initialization, state management, and cleanup.
 * 
 * @example
 * ```typescript
 * const orchestrator = new KaigenticChatOrchestrator({
 *   agentName: 'my-agent',
 *   apiBaseUrl: 'https://api.example.com',
 *   debug: true
 * });
 * 
 * await orchestrator.init();
 * orchestrator.show();
 * ```
 */
export class KaigenticChatOrchestrator implements OrchestratorInstance {
  private configManager: ConfigManager;
  private messageBridge: MessageBridge;
  private iframeManager: IframeManager | null = null;
  private state: OrchestratorState;
  private eventHandlers = new Map<string, Set<Function>>();
  private eventCleanups: (() => void)[] = [];
  private isDestroyed = false;
  private logger = createLogger('Orchestrator');

  /**
   * Create a new orchestrator instance
   * @param config - Configuration options for the orchestrator
   */
  constructor(config: Partial<OrchestratorConfig>) {
    // Initialize configuration manager
    this.configManager = new ConfigManager(config);
    const validatedConfig = this.configManager.getConfig();
    const layout = this.configManager.getLayout();

    // Initialize message bridge
    this.messageBridge = new MessageBridge(MessageSources.ORCHESTRATOR, {
      allowedOrigins: validatedConfig.allowedOrigins,
      debug: validatedConfig.debug
    });

    // Initialize state
    this.state = {
      initialized: false,
      visible: false,
      minimized: false,
      launcher: {
        visible: true,
        position: layout.position,
        hasUnreadBadge: false,
        unreadCount: 0,
        isAnimating: false
      },
      iframe: {
        loaded: false,
        visible: false,
        communicating: false,
        lastMessageTime: 0,
        position: layout.position,
        dimensions: {
          width: layout.width || 400,
          height: layout.height || 600
        }
      },
      config: validatedConfig,
      errors: []
    };

    // Setup message bridge handlers
    this.setupMessageHandlers();

    // Configure logger based on debug setting
    this.logger.setLevel(validatedConfig.debug ? 3 : 2); // DEBUG : INFO
    
    this.logger.info('Initialized with config', this.configManager.getConfigSummary());
  }

  /**
   * Initialize the orchestrator (create UI components)
   */
  async init(): Promise<void> {
    if (this.state.initialized || this.isDestroyed) {
      return;
    }

    try {
      // Wait for DOM to be ready
      await waitForDOMReady();

      const config = this.configManager.getConfig();
      const layout = this.configManager.getLayout();

      // Skip creating external launcher - ChatInterface handles its own launcher in overlay mode
      // The iframe contains the ChatInterface which will manage the floating widget behavior
      
      // Initialize launcher state for compatibility
      this.state.launcher = {
        visible: true,
        position: layout.position,
        hasUnreadBadge: false,
        unreadCount: 0,
        isAnimating: false
      };

      // Create iframe manager
      this.iframeManager = new IframeManager(config, layout);
      this.iframeManager.setMessageBridge(this.messageBridge);

      // Setup iframe load handler
      this.iframeManager.onLoad(() => {
        this.handleIframeReady('load');
      });

      // Setup iframe state change handler
      this.iframeManager.onStateChange((iframeState) => {
        this.state.iframe = iframeState;
        this.emit('iframe-state-change', iframeState);
      });

      // Create iframe
      await this.iframeManager.create();

      // Setup global event handlers
      this.setupGlobalEventHandlers();

      // Setup widget state message handlers
      this.setupWidgetStateHandlers();

      this.state.initialized = true;
      this.emit('initialized', { config: this.configManager.getConfigSummary() });

      this.logger.info('Initialization complete');

    } catch (error) {
      const orchestratorError = ErrorHandler.createInitError(
        'Failed to initialize orchestrator',
        'KaigenticChatOrchestrator',
        error
      );
      this.state.errors.push(orchestratorError.message);
      this.emit('error', { error: orchestratorError.message, context: 'initialization' });
      throw orchestratorError;
    }
  }

  /**
   * Show the chat interface (triggers ChatInterface to expand)
   */
  show(): void {
    if (!this.state.initialized || !this.iframeManager) {
      this.logger.warn('Cannot show: not initialized');
      return;
    }

    // ChatInterface handles the widget expansion and will send state updates
    this.iframeManager.sendMessage(MessageTypes.SHOW_WIDGET);
    
    // State will be updated when we receive widget-state-change message
  }

  /**
   * Hide the chat interface (triggers ChatInterface to minimize to launcher)
   */
  hide(): void {
    if (!this.iframeManager) return;

    // ChatInterface handles the widget minimization and will send state updates
    this.iframeManager.sendMessage(MessageTypes.HIDE_WIDGET);
    
    // State will be updated when we receive widget-state-change message
  }

  /**
   * Toggle chat interface visibility
   */
  toggle(): void {
    if (this.state.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<OrchestratorConfig>): void {
    const validation = this.configManager.updateConfig(config);
    
    if (!validation.isValid) {
      this.logger.error('Configuration update failed', validation.errors);
      this.emit('config-error', { errors: validation.errors, warnings: validation.warnings });
      return;
    }

    if (validation.warnings.length > 0) {
      this.logger.warn('Configuration warnings', validation.warnings);
    }

    // Update iframe configuration
    if (this.iframeManager) {
      this.iframeManager.updateConfig(config);
    }

    // Update state
    this.state.config = this.configManager.getConfig();
    
    this.emit('config-updated', { config: this.configManager.getConfigSummary() });
  }

  /**
   * Update layout configuration
   */
  updateLayout(layout: Partial<import('../types').OverlayLayout>): void {
    const validation = this.configManager.updateLayout(layout);
    
    if (!validation.isValid) {
      this.logger.error('Layout update failed', validation.errors);
      this.emit('layout-error', { errors: validation.errors, warnings: validation.warnings });
      return;
    }

    const newLayout = this.configManager.getLayout();

    // Launcher is now handled by ChatInterface - update will be sent via message

    // Update iframe
    if (this.iframeManager) {
      this.iframeManager.updateLayout(newLayout);
    }

    this.emit('layout-updated', { layout: newLayout });
  }

  /**
   * Get current configuration
   */
  getConfig(): OrchestratorConfig {
    return this.configManager.getConfig();
  }

  /**
   * Check if orchestrator is visible
   */
  isVisible(): boolean {
    return this.state.visible;
  }

  /**
   * Check if orchestrator is initialized
   */
  isInitialized(): boolean {
    return this.state.initialized;
  }

  /**
   * Add event handler
   */
  on(event: string, callback: (data: unknown) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(callback);
  }

  /**
   * Remove event handler
   */
  off(event: string, callback?: (data: unknown) => void): void {
    const handlers = this.eventHandlers.get(event);
    if (!handlers) return;

    if (callback) {
      handlers.delete(callback);
    } else {
      handlers.clear();
    }
  }

  /**
   * Emit event to handlers
   */
  emit(event: string, data?: unknown): void {
    const handlers = this.eventHandlers.get(event);
    if (!handlers || handlers.size === 0) return;

    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`[KaigenticChatOrchestrator] Event handler error for ${event}:`, error);
      }
    });
  }

  /**
   * Get launcher element (now handled by ChatInterface in iframe)
   */
  getLauncher(): HTMLElement | null {
    return this.iframeManager?.getElement() || null;
  }

  /**
   * Get iframe element
   */
  getIframe(): HTMLIFrameElement | null {
    return this.iframeManager?.getElement() || null;
  }

  /**
   * Get current state
   */
  getState(): OrchestratorState {
    return { ...this.state };
  }

  /**
   * Inject a user message into the chat interface
   */
  injectUserMessage(content: string): void {
    if (!this.state.initialized || !this.iframeManager) {
      console.warn('[KaigenticChatOrchestrator] Cannot inject user message: not initialized');
      return;
    }

    this.iframeManager.sendMessage(MessageTypes.INJECT_USER_MESSAGE, {
      content
    });
  }

  /**
   * Inject an agent response into the chat interface
   */
  async injectAgentResponse(content: string | object | (string | object)[]): Promise<void> {
    if (!this.state.initialized || !this.iframeManager) {
      console.warn('[KaigenticChatOrchestrator] Cannot inject agent response: not initialized');
      return;
    }

    return new Promise((resolve, reject) => {
      // Send injection message to iframe
      this.iframeManager!.sendMessage(MessageTypes.INJECT_AGENT_RESPONSE, {
        content
      });

      // Set a timeout for the injection to complete
      // Since injection is async in the ChatInterface, we'll assume it completes quickly
      setTimeout(() => {
        resolve();
      }, REQUEST_TIMEOUT);
    });
  }

  /**
   * Clear the chat interface
   */
  clearChat(): void {
    if (!this.state.initialized || !this.iframeManager) {
      console.warn('[KaigenticChatOrchestrator] Cannot clear chat: not initialized');
      return;
    }

    this.iframeManager.sendMessage(MessageTypes.CLEAR_CHAT, {});
  }

  /**
   * Destroy orchestrator and cleanup
   */
  destroy(): void {
    if (this.isDestroyed) return;

    this.logger.info('Destroying orchestrator instance');
    this.isDestroyed = true;

    try {
      // Emit destroy event before cleanup
      this.emit('destroyed', {});

      // Clean up iframe manager
      if (this.iframeManager) {
        this.iframeManager.destroy();
        this.iframeManager = null;
      }

      // Clean up message bridge
      this.messageBridge.destroy();

      // Clean up global event listeners
      this.eventCleanups.forEach(cleanup => {
        try {
          cleanup();
        } catch (error) {
          this.logger.warn('Error during event cleanup', error);
        }
      });
      this.eventCleanups.length = 0;

      // Clear event handlers
      this.eventHandlers.clear();

      // Reset state
      this.state = {
        initialized: false,
        visible: false,
        minimized: false,
        launcher: {
          visible: false,
          position: 'bottom-right',
          hasUnreadBadge: false,
          unreadCount: 0,
          isAnimating: false
        },
        iframe: {
          loaded: false,
          visible: false,
          communicating: false,
          lastMessageTime: 0,
          position: 'bottom-right',
          dimensions: { width: 400, height: 600 }
        },
        config: this.configManager.getConfig(),
        errors: []
      };

      this.logger.info('Orchestrator destroyed successfully');
    } catch (error) {
      this.logger.errorWithContext('Error during destruction', error);
    }
  }

  /**
   * Setup message bridge handlers
   */
  private setupMessageHandlers(): void {
    // Handle ready message from iframe
    this.messageBridge.on(MessageTypes.READY, () => {
      this.state.iframe.communicating = true;
      this.state.iframe.lastMessageTime = Date.now();
      this.handleIframeReady('ready');
    });

    // Forward CHAT_INTERFACE_READY as a public event. This fires exactly once
    // per lifecycle, after the full handshake (overlay config + iframe render),
    // so it's the cleanest signal for integrators that need a single readiness
    // gate. IframeManager subscribes to the same message type for its own
    // visibility logic — MessageBridge.on() supports multiple subscribers.
    this.messageBridge.on(MessageTypes.CHAT_INTERFACE_READY, (message) => {
      const dataMsg = message as DataMessage;
      this.emit('chat-interface-ready', { timestamp: dataMsg.data?.timestamp });
    });

    // Handle chat events from iframe
    this.messageBridge.on(MessageTypes.MESSAGE_SENT, (message) => {
      this.emit('message-sent', (message as DataMessage).data);
    });

    this.messageBridge.on(MessageTypes.MESSAGE_RECEIVED, (message) => {
      this.emit('message-received', (message as DataMessage).data);
    });

    this.messageBridge.on(MessageTypes.CONVERSATION_STARTED, (message) => {
      this.emit('conversation-started', (message as DataMessage).data);
    });

    this.messageBridge.on(MessageTypes.ERROR, (message) => {
      const dataMsg = message as DataMessage;
      const error = dataMsg.data?.error || 'Unknown error from iframe';
      this.state.errors.push(error);
      this.emit('error', { error, source: 'iframe' });
    });

    this.messageBridge.on(MessageTypes.UNREAD_COUNT_CHANGE, (message) => {
      // Unread count is now handled by ChatInterface internally
      const dataMsg = message as DataMessage;
      const count = dataMsg.data?.count || 0;
      
      // Update launcher state for compatibility
      this.state.launcher.unreadCount = count;
      this.state.launcher.hasUnreadBadge = count > 0;
      
      this.emit('unread-count-change', { count });
    });

    // STATE_CHANGE has been replaced by WIDGET_STATE_CHANGE
    
    // Handle version request from iframe
    // Listen for version requests using window message event
    const handleVersionRequest = (event: MessageEvent) => {
      if (event.data?.type === 'version-request' && event.data?.source === 'kaigentic-chat') {
        const { requestId } = event.data.data || {};
        
        // Send back orchestrator version
        if (event.source && requestId) {
          (event.source as Window).postMessage({
            type: 'version-response',
            data: {
              requestId,
              source: 'JS-Orchestrator',
              version: VERSION
            }
          }, event.origin);
          
          // If we're in an iframe, forward the request to parent
          if (window.parent !== window) {
            window.parent.postMessage(event.data, '*');
          }
        }
      }
    };
    
    window.addEventListener('message', handleVersionRequest);
    this.eventCleanups.push(() => window.removeEventListener('message', handleVersionRequest));
  }

  /**
   * Setup global event handlers
   */
  private setupGlobalEventHandlers(): void {
    // Handle page visibility changes
    const visibilityCleanup = addEventListener(document, 'visibilitychange', () => {
      if (document.hidden) {
        this.emit('page-hidden', {});
      } else {
        this.emit('page-visible', {});
      }
    });
    this.eventCleanups.push(visibilityCleanup);

    // Handle window resize with debouncing
    const debouncedResizeHandler = debounce(() => {
      this.emit('window-resize', {
        width: window.innerWidth,
        height: window.innerHeight
      });
    }, 250); // 250ms debounce
    
    const resizeCleanup = addEventListener(window, 'resize', debouncedResizeHandler);
    this.eventCleanups.push(resizeCleanup);

    // Handle before unload (page refresh/close)
    const beforeUnloadCleanup = addEventListener(window, 'beforeunload', () => {
      this.emit('page-unload', {});
    });
    this.eventCleanups.push(beforeUnloadCleanup);
  }

  /**
   * Track widget state before fullscreen so we can restore it
   */
  private stateBeforeFullscreen: 'launcher' | 'expanded' | 'minimized' | 'fullscreen' = 'expanded';
  private stateBeforeComponentExpand: 'launcher' | 'expanded' | 'minimized' | 'fullscreen' = 'expanded';

  /**
   * Setup widget state message handlers for ChatInterface communication
   */
  private setupWidgetStateHandlers(): void {
    // Handle widget state changes from ChatInterface
    this.messageBridge.on(MessageTypes.WIDGET_STATE_CHANGE, (message) => {
      // If we're receiving widget state but communication isn't established, enable it now
      // This handles race conditions where ChatInterface loads before the READY message is processed
      // However, avoid re-initializing if we're just updating the iframe state (like during widget closure)
      if (!this.state.iframe.communicating) {
        this.state.iframe.communicating = true;
        this.state.iframe.lastMessageTime = Date.now();
        // Only send init message if this is the first communication, not during widget state updates
        const isFirstCommunication = !this.state.iframe.loaded;
        if (isFirstCommunication) {
          this.handleIframeReady('widget-state-fallback');
        }
      }
      
      if (!this.iframeManager) {
        console.warn('[Orchestrator] No iframe manager available for widget state change');
        return;
      }
      
      const data = (message as DataMessage).data;
      
      if (data && data.state) {
        
        // Always use dimensions provided by ChatInterface
        // ChatInterface has the actual backend overlay config
        if (data.dimensions) {
          this.iframeManager.handleWidgetStateChange(
            data.state, 
            data.dimensions, 
            data.animated !== false
          );
        } else {
          // Fallback: let IframeManager calculate dimensions
          // This should rarely happen as ChatInterface always sends dimensions
          console.warn('[Orchestrator] No dimensions provided for state:', data.state);
          this.iframeManager.handleWidgetStateChange(data.state);
        }
        
        // Check if position has changed and update layout if needed
        if (data.position && data.position !== this.state.iframe.position) {
          // Update iframe position using existing layout update mechanism
          this.iframeManager.updateLayout({ position: data.position });
          this.state.iframe.position = data.position;
        }
        
        // Update orchestrator state
        this.state.visible = data.state === 'expanded' || data.state === 'fullscreen';
        this.state.minimized = data.state === 'minimized' || data.state === 'launcher';
        
        
        // Emit state change event
        this.emit('widget-state-change', {
          state: data.state,
          dimensions: data.dimensions,
          position: data.position,
          visible: this.state.visible,
          minimized: this.state.minimized
        });
      } else {
        console.warn('[Orchestrator] Invalid widget state change data:', data);
      }
    });

    // Handle fullscreen expanded - make iframe fullscreen on parent page
    this.messageBridge.on(MessageTypes.FULLSCREEN_EXPANDED, (message) => {
      this.logger.debug('[Orchestrator] FULLSCREEN_EXPANDED handler called');

      if (!this.iframeManager) {
        console.warn('[Orchestrator] No iframe manager available for fullscreen expanded');
        return;
      }

      const data = (message as DataMessage).data;

      // Store resize handle styles from ChatInterface if provided
      if (data && data.resizeHandleStyles) {
        this.logger.debug('[Orchestrator] Received resize handle styles:', data.resizeHandleStyles);
        this.iframeManager.setResizeHandleStyles(data.resizeHandleStyles);
      }

      // Save current widget state before going fullscreen
      this.stateBeforeFullscreen = this.iframeManager.getCurrentWidgetState();
      this.logger.debug('[Orchestrator] Saving state before fullscreen:', this.stateBeforeFullscreen);

      // Make iframe fullscreen on the parent page
      const fullscreenDimensions = {
        width: window.innerWidth,
        height: window.innerHeight,
        position: {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        },
        borderRadius: 0,
        zIndex: this.state.config.layout?.zIndex || 9999
      };

      this.logger.debug('[Orchestrator] Making iframe fullscreen with parent viewport dimensions:', fullscreenDimensions);

      // Send parent viewport dimensions to iframe BEFORE resizing
      // This ensures ChatInterface has accurate dimensions for fullscreen calculations
      this.messageBridge.send('parent-resize', {
        width: fullscreenDimensions.width,
        height: fullscreenDimensions.height
      });

      // Set expanded class synchronously — resizeIframe uses requestAnimationFrame
      // which can be overridden by a widget-state-change message arriving before the rAF fires
      const iframeEl = this.iframeManager.getElement();
      if (iframeEl) {
        setIframeStateClass(iframeEl, 'expanded');
      }

      // Use 'expanded' state for fullscreen — the iframe needs expanded state
      // regardless of what state it was in before (e.g., minimized)
      this.iframeManager.resizeIframe(fullscreenDimensions, 'expanded', true);

      // Enter fullscreen mode to show resize handles
      this.iframeManager.enterFullscreenMode();

      // Emit fullscreen expanded event
      this.emit('fullscreen-expanded', {
        width: fullscreenDimensions.width,
        height: fullscreenDimensions.height
      });
    });

    // Handle fullscreen collapsed - restore iframe to previous state
    this.messageBridge.on(MessageTypes.FULLSCREEN_COLLAPSED, (message) => {
      this.logger.debug('[Orchestrator] FULLSCREEN_COLLAPSED handler called');

      if (!this.iframeManager) {
        console.warn('[Orchestrator] No iframe manager available for fullscreen collapsed');
        return;
      }

      // Restore iframe to the state it was in before fullscreen
      this.logger.debug('[Orchestrator] Restoring to state before fullscreen:', this.stateBeforeFullscreen);

      // Get dimensions for the previous state
      const restoredDimensions = this.iframeManager.getResponsiveDimensions(this.stateBeforeFullscreen);
      this.logger.debug('[Orchestrator] Restoring iframe with dimensions:', restoredDimensions);

      this.iframeManager.resizeIframe(restoredDimensions, this.stateBeforeFullscreen, true);

      // Exit fullscreen mode to hide resize handles
      this.iframeManager.exitFullscreenMode();

      // Emit fullscreen collapsed event
      this.emit('fullscreen-collapsed', {});
    });

    // Handle fullscreen resize updates
    this.messageBridge.on(MessageTypes.FULLSCREEN_RESIZE, (message) => {
      this.logger.debug('[Orchestrator] FULLSCREEN_RESIZE handler called', message);

      if (!this.iframeManager) {
        console.warn('[Orchestrator] No iframe manager available for fullscreen resize');
        return;
      }

      const data = (message as DataMessage).data;
      this.logger.debug('[Orchestrator] Fullscreen resize data:', data);

      if (data && typeof data.width === 'number' && typeof data.height === 'number') {
        // Resize the iframe to match the fullscreen window dimensions
        const dimensions = {
          width: data.width,
          height: data.height,
          position: {
            top: data.top || 0,
            left: data.left || 0
          },
          borderRadius: 0,
          zIndex: this.state.config.layout?.zIndex || 9999
        };

        this.logger.debug('[Orchestrator] Resizing iframe to dimensions:', dimensions);
        // Use 'expanded' during fullscreen — stateBeforeFullscreen is for restore only
        this.iframeManager.resizeIframe(dimensions, 'expanded', false);

        // Emit fullscreen resize event
        this.emit('fullscreen-resize', {
          width: data.width,
          height: data.height,
          top: data.top,
          left: data.left,
          preset: data.preset
        });
      } else {
        console.warn('[Orchestrator] Invalid fullscreen resize data:', data);
      }
    });

    // Handle component expand — resize iframe to full viewport so the modal has room
    this.messageBridge.on(MessageTypes.COMPONENT_EXPAND, () => {
      this.logger.info('COMPONENT_EXPAND handler called');

      if (!this.iframeManager) {
        this.logger.warn('No iframe manager available for component expand');
        return;
      }

      // Save current state so we can restore on collapse (separate from fullscreen state)
      this.stateBeforeComponentExpand = this.iframeManager.getCurrentWidgetState();

      const expandDimensions = {
        width: window.innerWidth,
        height: window.innerHeight,
        position: { top: 0, left: 0, right: 0, bottom: 0 },
        borderRadius: 0,
        zIndex: this.state.config.layout?.zIndex || 9999,
      };

      // Notify iframe of parent dimensions before resizing
      this.messageBridge.send('parent-resize', {
        width: expandDimensions.width,
        height: expandDimensions.height,
      });

      this.iframeManager.resizeIframe(expandDimensions, this.stateBeforeComponentExpand, true);
      this.emit('component-expand', {});
    });

    // Handle component collapse — restore iframe to previous size
    this.messageBridge.on(MessageTypes.COMPONENT_COLLAPSE, () => {
      this.logger.info('COMPONENT_COLLAPSE handler called');

      if (!this.iframeManager) {
        this.logger.warn('No iframe manager available for component collapse');
        return;
      }

      const restoredDimensions = this.iframeManager.getResponsiveDimensions(this.stateBeforeComponentExpand);
      this.iframeManager.resizeIframe(restoredDimensions, this.stateBeforeComponentExpand, true);
      this.emit('component-collapse', {});
    });

    // Handle widget dimensions updates
    // Note: WIDGET_DIMENSIONS_UPDATE, LAUNCHER_CLICKED, WIDGET_OPENED, WIDGET_CLOSED,
    // and WIDGET_MINIMIZED are now redundant - all functionality handled by WIDGET_STATE_CHANGE

  }

  /**
   * Handle iframe ready state.
   *
   * The `source` discriminator lets external subscribers tell which path
   * triggered the event. In a normal lifecycle the DOM-load and READY-message
   * paths both fire. Integrators that want a single, once-per-lifecycle
   * readiness signal should subscribe to 'chat-interface-ready' instead.
   */
  private handleIframeReady(source: IframeReadySource): void {
    if (!this.iframeManager) return;
    this.emit('iframe-ready', { source });
  }
}